<template>
  <span class="relative inline-flex items-center align-middle" @mouseenter="showOnHover" @mouseleave="hideOnHover">
    <button
      ref="triggerEl"
      type="button"
      class="shrink-0 -m-1 p-1 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors"
      :aria-label="$t('live.infoTooltip.label')"
      @click.stop="toggleOnTap"
    >
      <Info class="w-3.5 h-3.5" />
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="popoverEl"
        class="fixed z-[110] w-[min(280px,calc(100vw-2rem))] rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl p-3 text-[12px] leading-relaxed text-[hsl(var(--foreground)/0.85)] font-normal normal-case"
        :style="{ top: `${pos.top}px`, left: `${pos.left}px` }"
        @mouseenter="showOnHover"
        @mouseleave="hideOnHover"
      >
        {{ text }}
      </div>
    </Teleport>
  </span>
</template>

<script lang="ts">
import { ref as sharedRef } from "vue";
// Общий для всех экземпляров InfoTooltip: хранит id последнего открытого тапом
// поповера, чтобы открытие второго закрывало первый (иначе соседние бейджи
// перекрываются открытыми поповерами).
const activeTapId = sharedRef<symbol | null>(null);
</script>

<script setup lang="ts">
import { onBeforeUnmount, reactive, ref, watch } from "vue";
import { Info } from "lucide-vue-next";

defineProps<{ text: string }>();

const instanceId = Symbol();
const triggerEl = ref<HTMLElement | null>(null);
const popoverEl = ref<HTMLElement | null>(null);
const open = ref(false);
const pos = reactive({ top: 0, left: 0 });
// На тач-устройствах браузер эмулирует mouseenter перед тапом, но не шлёт
// mouseleave до следующего тапа в другое место - без этой проверки состояние
// "открыто" залипало бы, а повторный тап по кнопке не закрывал бы поповер.
const supportsHover = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false;
let hoverOpen = false;
let tapOpen = false;

const GAP = 6;
const MARGIN = 8;

const place = () => {
  const trigger = triggerEl.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const popoverWidth = Math.min(280, window.innerWidth - MARGIN * 2);
  let left = rect.left + rect.width / 2 - popoverWidth / 2;
  left = Math.min(Math.max(left, MARGIN), window.innerWidth - popoverWidth - MARGIN);
  // Реальная высота поповера неизвестна до рендера; 120px - оценка с запасом,
  // чтобы заранее решить, показывать сверху или снизу.
  const estimatedHeight = 120;
  const spaceBelow = window.innerHeight - rect.bottom;
  const top = spaceBelow >= estimatedHeight + GAP ? rect.bottom + GAP : rect.top - estimatedHeight - GAP;
  pos.top = Math.max(MARGIN, top);
  pos.left = left;
};

const closeOnOutsideClick = (e: MouseEvent) => {
  const target = e.target as Node;
  if (triggerEl.value?.contains(target) || popoverEl.value?.contains(target)) return;
  tapOpen = false;
  if (activeTapId.value === instanceId) activeTapId.value = null;
  sync();
};

const sync = () => {
  const shouldBeOpen = hoverOpen || tapOpen;
  if (shouldBeOpen && !open.value) {
    open.value = true;
    requestAnimationFrame(place);
  } else if (!shouldBeOpen && open.value) {
    open.value = false;
  }
  if (tapOpen) {
    // Отложено, чтобы этот же клик не всплыл в обработчик и не закрыл поповер сразу.
    setTimeout(() => document.addEventListener("click", closeOnOutsideClick), 0);
  } else {
    document.removeEventListener("click", closeOnOutsideClick);
  }
};

const showOnHover = () => {
  if (!supportsHover) return;
  hoverOpen = true;
  sync();
};
const hideOnHover = () => {
  if (!supportsHover) return;
  hoverOpen = false;
  sync();
};
const toggleOnTap = () => {
  tapOpen = !tapOpen;
  activeTapId.value = tapOpen ? instanceId : null;
  sync();
};

watch(activeTapId, (id) => {
  if (id !== instanceId && tapOpen) {
    tapOpen = false;
    sync();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick);
});
</script>
